var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef } from 'react';
import { usePopoverState, Popover, PopoverDisclosure } from 'reakit';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import PopoverArrow from './ReakitOverrides/DejargonatorArrow';
import { Box } from '../Box';
import { TextHeadingDEPRECATED, TextBodyDEPRECATED, } from '../TypographyDEPRECATED';
import { calculateArrowOrientation, extractTranslationValues } from './utils';
import { a11y } from './strings';
import { useMediaSize } from '../tools/utils';
const DejargHeading = styled(TextHeadingDEPRECATED) `
  ${({ theme: { space, colors, font } }) => `
    && {
      color: ${colors.white};
      font-size: ${font.body.sm.size};
      line-height: ${font.body.sm.lineHeight};
      display: block;
      
      margin-bottom: ${space.xs};

      &:focus {
        outline: none;
      }
    }
  `}
`;
const DejargBody = styled(TextBodyDEPRECATED) `
  ${({ theme: { colors, font } }) => `
  && {
    color: ${colors['gray-400']};
    font-size: ${font.body.sm.size};
    line-height: ${font.body.sm.lineHeight};

    display: block;
    white-space: pre-wrap;

    &:focus {
      outline: none;
    }
  }
  `}
`;
const StyledPopover = styled(Popover) `
  && {
    ${({ theme: { colors, media, space } }) => {
    return `
        width: 100vw;
        min-height: 50px;
        padding: ${space.none};
        background: ${colors.midnightblue};
        border-radius: 4px;
        color: ${colors['slate-5']};
        z-index: 5;
        box-shadow: 0 4px 8px -2px rgba(0,0,0,0.16), 0 10px 12px -4px rgba(0,0,0,0.08);

        ${media.up('md')} {
          width: 360px;
        }
      }
    }
    `;
}}
  }
`;
const StyledTrigger = styled.div `
  && {
    background: ${({ theme: { colors }, $highlightTrigger }) => $highlightTrigger ? colors['gold-100'] : 'transparent'};
    cursor: help;
  }
`;
const TriggerText = styled(TextBodyDEPRECATED) `
  && {
    display: inline-block;
    padding: 0;
    border: none;
    border-bottom: ${(props) => `1px dotted ${props.theme.colors['slate-3']}`};
    font-size: 14px;
    font-family: ${({ theme }) => theme.fonts.regular};
  }
`;
const IconWrapper = styled.div `
  && {
    display: inline-flex;
  }
`;
const DejargContainer = styled.div `
  && {
    display: inline-block;
    line-height: 12px;
  }
`;
const StyledPopoverDisclosure = styled(PopoverDisclosure) `
  && {
    min-width: auto;
    border: none;
    background: transparent;
    padding: 0;
  }
`;
/** Component is a blank space that allows the cursor to move from the
 * dejarg trigger to the dejarg itself without the dejarg closing */
const ExtraSpace = styled.div `
  ${({ placement }) => {
    if (placement === 'top') {
        return `
        top: -35px;
      `;
    }
    if (placement === 'left') {
        return `
        top: -10px;
        left: -30px;
      `;
    }
    if (placement === 'right') {
        return `
        top: -10px;
        right: -30px;
      `;
    }
    return '';
}}

  ${({ placement }) => `
    width: ${placement === 'top' || placement === 'bottom' ? '100%' : '30px'};
    height:${placement === 'left' || placement === 'right' ? '50px' : '35px'};
    position: absolute;
    cursor: default;
    z-index: 5;
  `}
`;
const DisclosureWrapper = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
`;
const getTriggerElement = (trigger) => {
    switch (trigger.variant) {
        case 'text':
            return (React.createElement(TriggerText, { tag: "span", size: "sm" }, trigger.content));
        case 'icon':
            return (React.createElement(IconWrapper, { "data-testid": "trigger-icon" }, trigger.content));
        default:
            // default to returning the content directly - element variant handled here
            return trigger.content;
    }
};
const VisuallyHiddenDescription = styled.p(({ theme: { screenReader } }) => `
    ${screenReader.srOnlyInline}
    
    position: fixed;
  `);
const Dejargonator = (_a) => {
    var { heading, body, trigger, ariaLabelPopover, placement = 'bottom', popoverId, isPopoverFixedPosition = false, headingTag } = _a, others = __rest(_a, ["heading", "body", "trigger", "ariaLabelPopover", "placement", "popoverId", "isPopoverFixedPosition", "headingTag"]);
    const [responsivePlacement, setResponsivePlacement] = useState(placement);
    const isMobile = useMediaSize('SmDown');
    useEffect(() => {
        // If mobile view, we will only respect placement prop if "top" or "bottom".
        // Any other value, we default to "bottom"
        if (isMobile) {
            setResponsivePlacement(placement === 'top' || placement === 'bottom' ? placement : 'bottom');
        }
        else {
            // If viewport changes from mobile to desktop, we want to respect the placement prop again
            setResponsivePlacement(placement);
        }
    }, [isMobile, placement]);
    const [arrowOrientation, setArrowOrientation] = useState('left');
    const popover = usePopoverState({
        placement: responsivePlacement,
        baseId: popoverId,
        gutter: responsivePlacement === 'top' || responsivePlacement === 'bottom'
            ? 20
            : 25,
        unstable_fixed: isPopoverFixedPosition,
    });
    const extraSpaceRef = React.createRef();
    const popoverRef = React.createRef();
    const { current: descriptionUUID } = React.useRef(`dejarg-description-${uuid()}`);
    /**
     * Don't show the popover until DOM updates are complete. This prevents the popover
     * from initially showing up in the wrong place while it waits to be repositioned by reakit.
     */
    const [showPopover, setShowPopover] = React.useState(true);
    // unstable_arrowStyles is the last DOM update made to correctly position the popover
    useEffect(() => {
        setShowPopover(popover.visible);
    }, [popover, popover.unstable_arrowStyles]);
    /**
     * Some Android devices trigger 'mouseenter' on touch. isTouchEvent is used to disable the  'mouseenter'
     * handler in these cases to prevent users from having to click twice to open the dejarg
     */
    const [isTouchEvent, setIsTouchEvent] = useState(false);
    useEffect(() => {
        popover.place(responsivePlacement);
    }, [popover, responsivePlacement]);
    const initialFocus = useRef(null);
    const triggerRef = useRef(null);
    const [isBlurredOnTrigger, setIsBlurredOnTrigger] = React.useState(false);
    const handlePopoverBlur = (e) => {
        const isTrigger = e.relatedTarget === triggerRef.current;
        setIsBlurredOnTrigger(isTrigger);
        popover.hide();
    };
    const show = () => {
        if (!isTouchEvent && !popover.visible) {
            popover.show();
            setIsBlurredOnTrigger(false);
        }
    };
    const hide = () => {
        if (popover.visible) {
            popover.hide();
        }
    };
    useEffect(() => {
        var _a, _b, _c, _d;
        const arrowTranslationValues = extractTranslationValues(popover.unstable_arrowStyles.transform);
        const newArrowOrientation = calculateArrowOrientation(popover.placement, (_b = (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0, (_d = (_c = popoverRef.current) === null || _c === void 0 ? void 0 : _c.clientHeight) !== null && _d !== void 0 ? _d : 0, arrowTranslationValues);
        if (newArrowOrientation !== arrowOrientation) {
            setArrowOrientation(newArrowOrientation);
        }
    }, [popoverRef]);
    const onDejargElementMouseLeave = e => {
        var _a, _b;
        e.preventDefault();
        e.stopPropagation();
        // If the mouse enters an element that is not one of the related dejarg elements,
        // then we hide the dejarg. Otherwise it stays open while user moves the mouse
        // from the trigger to the dejarg, and vice versa.
        if (e.relatedTarget !== extraSpaceRef.current &&
            e.relatedTarget !== ((_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.children[0].children[0]) &&
            e.relatedTarget !== ((_b = popoverRef.current) === null || _b === void 0 ? void 0 : _b.children[1])) {
            hide();
        }
    };
    const handleTriggerOnClick = (e) => {
        setIsTouchEvent(false);
        const isNonClickEvent = e.detail === 0;
        if (isBlurredOnTrigger && !isNonClickEvent)
            e.preventDefault();
        setIsBlurredOnTrigger(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DejargContainer, null,
            React.createElement(DisclosureWrapper, null,
                React.createElement(StyledPopoverDisclosure, Object.assign({}, popover, { ref: triggerRef, onTouchStart: () => setIsTouchEvent(true), onMouseEnter: show, onMouseLeave: onDejargElementMouseLeave, onClick: handleTriggerOnClick, "data-testid": "mui-dejargonator-trigger-button", "aria-label": trigger.variant !== 'text' && 'topic' in trigger
                        ? a11y.getAriaLabelTrigger(trigger.topic)
                        : // Typescript could not properly narrow type
                            trigger.ariaLabel || undefined, "aria-describedby": descriptionUUID, focusable: true }),
                    React.createElement(StyledTrigger, { "data-testid": "mui-dejargonator-trigger", "$highlightTrigger": popover.visible &&
                            trigger.variant !== 'icon' &&
                            !trigger.disableTriggerHighlight }, getTriggerElement(trigger))),
                popover.visible && (React.createElement(ExtraSpace, { ref: extraSpaceRef, placement: popover.placement.split('-')[0], onMouseLeave: onDejargElementMouseLeave, "data-testid": "mui-dejarg-trigger-bridge" }))),
            React.createElement(StyledPopover, Object.assign({ role: "region", ref: popoverRef }, popover, { "aria-label": ariaLabelPopover, hideOnEsc: true, hideOnClickOutside: true, onMouseLeave: onDejargElementMouseLeave, unstable_initialFocusRef: initialFocus, unstable_finalFocusRef: triggerRef, onBlur: handlePopoverBlur, "data-testid": "dejarg-content-wrapper", "$placement": popover.placement }, (!showPopover && { style: { opacity: 0 } }), others),
                React.createElement(PopoverArrow, Object.assign({}, popover, { orientation: arrowOrientation })),
                React.createElement(Box, { position: "relative", p: "md" },
                    !!heading && (React.createElement(DejargHeading, { ref: initialFocus, tag: headingTag || 'h1', size: "sm", fontWeight: "bold", tabIndex: -1, "data-testid": "dejarg-header" }, heading)),
                    React.createElement(DejargBody, Object.assign({ tag: "span", size: "sm", "data-testid": "dejarg-content" }, (heading ? {} : { ref: initialFocus, tabIndex: -1 })), body)))),
        trigger.variant === 'text' && (React.createElement(VisuallyHiddenDescription, { id: descriptionUUID, "aria-hidden": "true", "data-testid": "mui-dejargonator-hidden-description" }, trigger.ariaDescription || a11y.ariaDescriptionTrigger))));
};
export { Dejargonator };
//# sourceMappingURL=Dejargonator.js.map